"use client";

import { useState, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
    ArrowRight,
    Building2,
    Eye,
    EyeOff,
    Gift,
    Loader2,
    Lock,
    Mail,
    Phone,
    User,
} from "lucide-react";

import { createClient } from "@oxpa/shared/lib/supabase/client";
import { Button } from "@oxpa/shared/ui/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@oxpa/shared/ui/form";
import { Input } from "@oxpa/shared/ui/input";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@oxpa/shared/ui/card";
import { Checkbox } from "@oxpa/shared/ui/checkbox";
import { useToast } from "@oxpa/shared/hooks/use-toast";
import Link from "next/link";
import { useDaDataCompanySuggest } from "@oxpa/shared/hooks/use-dadata";
import { OxpaLogo } from "@oxpa/shared/ui/oxpa-logo";

const formSchema = z.object({
    companyName: z.string().min(
        3,
        "Название компании должно содержать минимум 3 символа",
    ),
    inn: z.string().optional(),
    firstName: z.string().min(2, "Введите ваше имя"),
    lastName: z.string().min(2, "Введите вашу фамилию"),
    phone: z.string().min(10, "Введите корректный номер телефона").optional(),
    email: z.string().email("Введите корректный email"),
    password: z.string().min(6, "Пароль должен содержать минимум 6 символов"),
    passwordConfirm: z.string(),
    acceptOffer: z.boolean().refine(val => val === true, "Необходимо принять условия"),
    acceptPrivacy: z.boolean().refine(val => val === true, "Необходимо согласие на обработку данных"),
}).refine((data) => data.password === data.passwordConfirm, {
    message: "Пароли не совпадают",
    path: ["passwordConfirm"],
});

function RegisterForm() {
    const router = useRouter();
    const searchParams = useSearchParams();
    const refCode = searchParams.get("ref") || "";
    const { toast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [step, setStep] = useState<1 | 2>(1);
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
    const [daDataApplied, setDaDataApplied] = useState(false);

    const {
        loading: daDataLoading,
        results: daDataResults,
        suggestDebounced,
        reset: resetDaData,
    } = useDaDataCompanySuggest();

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            companyName: "",
            inn: "",
            firstName: "",
            lastName: "",
            phone: "",
            email: "",
            password: "",
            passwordConfirm: "",
            acceptOffer: false,
            acceptPrivacy: false,
        },
    });

    async function onSubmit(values: z.infer<typeof formSchema>) {
        setIsLoading(true);

        try {
            // Вызываем наш B2B API Route для создания Тенанта и Admin-а (Service Role)
            const res = await fetch("/api/auth/register-b2b", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    email: values.email,
                    password: values.password,
                    companyName: values.companyName,
                    inn: values.inn,
                    firstName: values.firstName,
                    lastName: values.lastName,
                    phone: values.phone,
                    refCode: refCode || undefined,
                }),
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(
                    data.error || "Не удалось зарегистрировать компанию",
                );
            }

            // После успешного создания тенанта и юзера на беке, логинимся клиентом
            const supabase = createClient();
            const { error: signInError } = await supabase.auth
                .signInWithPassword({
                    email: values.email,
                    password: values.password,
                });

            if (signInError) {
                toast({
                    variant: "destructive",
                    title: "Учетная запись создана, но вход не удался",
                    description: "Пожалуйста, войдите в систему вручную.",
                });
                router.push("/login");
                return;
            }

            toast({
                title: "Регистрация успешна!",
                description: "Подготавливаем ваше рабочее пространство...",
            });

            // Редирект в визард онбординга для настройки рабочего пространства
            router.push("/onboarding/wizard");
            router.refresh();
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Ошибка регистрации",
                description: error.message ||
                    "Произошла непредвиденная ошибка.",
            });
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }

    const nextStep = async () => {
        // Валидируем только поля первого шага (Контактные данные)
        const valid = await form.trigger([
            "firstName",
            "lastName",
            "email",
            "phone",
            "password",
            "passwordConfirm",
        ]);
        if (valid) setStep(2);
    };

    const prevStep = () => setStep(1);

    const handleCompanyNameChange = (value: string) => {
        form.setValue("companyName", value);
        setDaDataApplied(false);
        if (value.length >= 3) {
            suggestDebounced(value);
        } else {
            resetDaData();
        }
    };

    const applyDaDataResult = (result: any) => {
        form.setValue("companyName", result.name);
        form.setValue("inn", result.inn);
        setDaDataApplied(true);
        resetDaData();
    };

    return (
        <div className="min-h-screen w-full flex items-center justify-center p-4 bg-[hsl(var(--portal-bg))] text-[hsl(var(--portal-text))] font-sans">
            <div className="w-full max-w-lg">
                {/* Бейдж реферала */}
                {refCode && (
                    <div className="mb-4 flex items-center justify-center gap-2 border border-[hsl(var(--portal-accent-muted))] bg-[hsl(var(--portal-surface))] px-4 py-2">
                        <Gift className="w-4 h-4 text-[hsl(var(--portal-accent))]" />
                        <span className="text-[hsl(var(--portal-text))] text-xs uppercase tracking-widest font-bold">
                            Вас пригласили — бонус 500 монеток
                        </span>
                    </div>
                )}
                <div className="text-center mb-8 flex flex-col items-center">
                    <Link
                        href="/"
                        className="flex items-center gap-3 font-mono text-3xl font-bold text-[hsl(var(--portal-text))] uppercase tracking-widest hover:text-[hsl(var(--portal-accent))] transition-colors"
                    >
                        <OxpaLogo className="h-8 w-8 text-[hsl(var(--portal-accent))]" />
                        ОХРА.ПРО
                    </Link>
                    <div className="flex items-center justify-center gap-2 mt-4 text-[10px] uppercase tracking-widest">
                        <div
                            className={`px-3 py-1 border ${
                                step === 1
                                    ? "border-[hsl(var(--portal-accent))] bg-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent-on))] font-bold"
                                    : "border-[hsl(var(--portal-border))] text-[hsl(var(--portal-text-muted))]"
                            }`}
                        >
                            ШАГ 1: АВТОР
                        </div>
                        <div className="w-8 h-px bg-[hsl(var(--portal-border))]" />
                        <div
                            className={`px-3 py-1 border ${
                                step === 2
                                    ? "border-[hsl(var(--portal-accent))] bg-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent-on))] font-bold"
                                    : "border-[hsl(var(--portal-border))] text-[hsl(var(--portal-text-muted))]"
                            }`}
                        >
                            ШАГ 2: БИЗНЕС
                        </div>
                    </div>
                </div>

                <Card className="bg-[hsl(var(--portal-surface))] border border-[hsl(var(--portal-border))] rounded-none relative overflow-hidden">
                                        <CardHeader className="space-y-1 border-b border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-bg-elevated))] p-8">
                        <CardTitle className="font-mono text-xl font-bold text-[hsl(var(--portal-text))] uppercase tracking-widest">
                            {step === 1
                                ? "Профиль Администратора"
                                : "Регистрация Компании"}
                        </CardTitle>
                        <CardDescription className="text-[hsl(var(--portal-text-muted))] text-xs">
                            {step === 1
                                ? "Создайте аккаунт владельца (Owner) для управления платформой"
                                : "Укажите название вашего ЧОП для инициализации изолированного аккаунта"}
                        </CardDescription>
                    </CardHeader>

                    <CardContent className="p-8">
                        <Form {...form}>
                            <form
                                onSubmit={form.handleSubmit(onSubmit)}
                                className="space-y-6"
                            >
                                {/* STEP 1: PERSONAL DETAILS */}
                                <div
                                    className={step === 1
                                        ? "space-y-6 block"
                                        : "hidden"}
                                >
                                    <div className="grid grid-cols-2 gap-4">
                                        <FormField
                                            control={form.control}
                                            name="firstName"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))] flex items-center gap-2">
                                                        <User className="w-3 h-3" />
                                                        {" "}
                                                        Имя
                                                    </FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            className="bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border))] text-[hsl(var(--portal-text))] h-12 rounded-none placeholder:text-[hsl(var(--portal-text-dim))] focus-visible:ring-1 focus-visible:ring-[hsl(var(--portal-accent))]"
                                                            placeholder="Иван"
                                                            {...field}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="lastName"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">
                                                        Фамилия
                                                    </FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            className="bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border))] text-[hsl(var(--portal-text))] h-12 rounded-none placeholder:text-[hsl(var(--portal-text-dim))] focus-visible:ring-1 focus-visible:ring-[hsl(var(--portal-accent))]"
                                                            placeholder="Иванов"
                                                            {...field}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <FormField
                                            control={form.control}
                                            name="email"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))] flex items-center gap-2">
                                                        <Mail className="w-3 h-3" />
                                                        {" "}
                                                        Email
                                                    </FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            className="bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border))] text-[hsl(var(--portal-text))] h-12 rounded-none placeholder:text-[hsl(var(--portal-text-dim))] focus-visible:ring-1 focus-visible:ring-[hsl(var(--portal-accent))]"
                                                            type="email"
                                                            placeholder="ceo@chop.ru"
                                                            {...field}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="phone"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))] flex items-center gap-2">
                                                        <Phone className="w-3 h-3" />
                                                        {" "}
                                                        Телефон
                                                    </FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            className="bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border))] text-[hsl(var(--portal-text))] h-12 rounded-none placeholder:text-[hsl(var(--portal-text-dim))] focus-visible:ring-1 focus-visible:ring-[hsl(var(--portal-accent))]"
                                                            placeholder="+7 (999) 000-00-00"
                                                            {...field}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <FormField
                                            control={form.control}
                                            name="password"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))] flex items-center gap-2">
                                                        <Lock className="w-3 h-3" />
                                                        {" "}
                                                        Пароль
                                                    </FormLabel>
                                                    <FormControl>
                                                        <div className="relative">
                                                            <Input
                                                                className="bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border))] text-[hsl(var(--portal-text))] h-12 rounded-none placeholder:text-[hsl(var(--portal-text-dim))] focus-visible:ring-1 focus-visible:ring-[hsl(var(--portal-accent))] pr-10"
                                                                type={showPassword
                                                                    ? "text"
                                                                    : "password"}
                                                                placeholder="******"
                                                                {...field}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    setShowPassword(
                                                                        !showPassword,
                                                                    )}
                                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--portal-text-muted))] hover:text-[hsl(var(--portal-text))] transition-colors focus:outline-none"
                                                            >
                                                                {showPassword
                                                                    ? (
                                                                        <EyeOff className="h-4 w-4" />
                                                                    )
                                                                    : (
                                                                        <Eye className="h-4 w-4" />
                                                                    )}
                                                            </button>
                                                        </div>
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="passwordConfirm"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">
                                                        Повторите пароль
                                                    </FormLabel>
                                                    <FormControl>
                                                        <div className="relative">
                                                            <Input
                                                                className="bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border))] text-[hsl(var(--portal-text))] h-12 rounded-none placeholder:text-[hsl(var(--portal-text-dim))] focus-visible:ring-1 focus-visible:ring-[hsl(var(--portal-accent))] pr-10"
                                                                type={showPasswordConfirm
                                                                    ? "text"
                                                                    : "password"}
                                                                placeholder="******"
                                                                {...field}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    setShowPasswordConfirm(
                                                                        !showPasswordConfirm,
                                                                    )}
                                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--portal-text-muted))] hover:text-[hsl(var(--portal-text))] transition-colors focus:outline-none"
                                                            >
                                                                {showPasswordConfirm
                                                                    ? (
                                                                        <EyeOff className="h-4 w-4" />
                                                                    )
                                                                    : (
                                                                        <Eye className="h-4 w-4" />
                                                                    )}
                                                            </button>
                                                        </div>
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>

                                    <div className="pt-4 flex items-center justify-between">
                                        <Link
                                            href="/login"
                                            className="text-[10px] text-[hsl(var(--portal-text-muted))] hover:text-[hsl(var(--portal-text))] uppercase tracking-widest underline underline-offset-4"
                                        >
                                            УЖЕ ЕСТЬ АККАУНТ?
                                        </Link>
                                        <Button
                                            type="button"
                                            onClick={nextStep}
                                            className="h-12 bg-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent-on))] hover:bg-[hsl(var(--portal-accent-hover))] rounded-none uppercase tracking-widest font-bold text-xs px-8"
                                        >
                                            ДАЛЕЕ{" "}
                                            <ArrowRight className="ml-2 h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>

                                {/* STEP 2: COMPANY DETAILS */}
                                <div
                                    className={step === 2
                                        ? "space-y-6 block"
                                        : "hidden"}
                                >
                                    <FormField
                                        control={form.control}
                                        name="companyName"
                                        render={({ field }) => (
                                            <FormItem className="relative">
                                                <div className="flex items-center justify-between">
                                                    <FormLabel className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))] flex items-center gap-2">
                                                        <Building2 className="w-3 h-3" />
                                                        {" "}
                                                        Название ЧОП
                                                    </FormLabel>
                                                    {daDataLoading && (
                                                        <Loader2 className="w-3 h-3 animate-spin text-[hsl(var(--portal-text-muted))]" />
                                                    )}
                                                </div>
                                                <FormControl>
                                                    <Input
                                                        className="bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border))] text-[hsl(var(--portal-text))] h-12 rounded-none placeholder:text-[hsl(var(--portal-text-dim))] focus-visible:ring-1 focus-visible:ring-[hsl(var(--portal-accent))] text-lg uppercase tracking-wide"
                                                        placeholder="ЧОП ВЫМПЕЛ"
                                                        value={field.value}
                                                        onChange={(e) =>
                                                            handleCompanyNameChange(
                                                                e.target.value,
                                                            )}
                                                    />
                                                </FormControl>
                                                <p className="text-xs text-[hsl(var(--portal-text-muted))] mt-2">
                                                    Это название будет
                                                    использоваться в заголовках
                                                    и документах.{" "}
                                                    {form.watch("inn")
                                                        ? `ИНН: ${
                                                            form.watch("inn")
                                                        }`
                                                        : ""}
                                                </p>

                                                {/* Подсказки DaData */}
                                                {daDataResults.length > 0 &&
                                                    !daDataApplied && (
                                                    <div className="absolute top-full left-0 right-0 z-50 mt-1 border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-surface))] overflow-hidden flex flex-col max-h-48 overflow-y-auto">
                                                        {daDataResults.map((
                                                            r,
                                                            i,
                                                        ) => (
                                                            <button
                                                                key={i}
                                                                type="button"
                                                                className="w-full text-left px-4 py-3 text-sm hover:bg-[hsl(var(--portal-surface-2))] transition-colors border-b border-[hsl(var(--portal-border))] last:border-b-0"
                                                                onClick={() =>
                                                                    applyDaDataResult(
                                                                        r,
                                                                    )}
                                                            >
                                                                <div className="font-bold text-[hsl(var(--portal-text))]">
                                                                    {r.name}
                                                                </div>
                                                                <div className="text-xs text-[hsl(var(--portal-text-muted))] mt-1">
                                                                    ИНН: {r.inn}
                                                                    {" "}
                                                                    |{" "}
                                                                    {r.director}
                                                                </div>
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    {/* В будущем здесь можно добавить ИНН и автокомплит по DaData */}
                                    <div className="border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-bg-elevated))] p-4 flex items-start gap-4 mt-6">
                                        <div className="w-8 h-8 border border-[hsl(var(--portal-border))] flex items-center justify-center flex-shrink-0">
                                            <Lock className="w-4 h-4 text-[hsl(var(--portal-text-muted))]" />
                                        </div>
                                        <div>
                                            <h4 className="text-xs font-bold uppercase tracking-widest text-[hsl(var(--portal-text))] mb-1">
                                                Изолированный Тенант
                                            </h4>
                                            <p className="text-[10px] text-[hsl(var(--portal-text-muted))] font-sans leading-relaxed">
                                                После регистрации для вашей
                                                компании будет создана
                                                защищенная рабочая область. Ваши
                                                данные на 100% изолированы от
                                                других участников платформы.
                                            </p>
                                        </div>
                                    </div>

                                    {/* Чекбокс принятия оферты */}
                                    <FormField
                                        control={form.control}
                                        name="acceptOffer"
                                        render={({ field }) => (
                                            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                                <FormControl>
                                                    <Checkbox
                                                        checked={field.value}
                                                        onCheckedChange={field.onChange}
                                                        className="border-[hsl(var(--portal-border-hover))] data-[state=checked]:bg-[hsl(var(--portal-accent))] data-[state=checked]:text-[hsl(var(--portal-accent-on))] data-[state=checked]:border-[hsl(var(--portal-accent))] h-5 w-5 mt-0.5 rounded-none"
                                                    />
                                                </FormControl>
                                                <div className="space-y-1 leading-none pt-0.5">
                                                    <FormLabel className="text-xs uppercase tracking-wide text-[hsl(var(--portal-text-secondary))] cursor-pointer">
                                                        Я принимаю условия{" "}
                                                        <Link
                                                            href="/offer"
                                                            target="_blank"
                                                            className="text-[hsl(var(--portal-accent))] underline underline-offset-2 hover:text-[hsl(var(--portal-accent-hover))]"
                                                        >
                                                            Публичной оферты
                                                        </Link>
                                                        {" "}и{" "}
                                                        <Link
                                                            href="/terms"
                                                            target="_blank"
                                                            className="text-[hsl(var(--portal-accent))] underline underline-offset-2 hover:text-[hsl(var(--portal-accent-hover))]"
                                                        >
                                                            Пользовательского соглашения
                                                        </Link>
                                                    </FormLabel>
                                                </div>
                                            </FormItem>
                                        )}
                                    />

                                    {/* Чекбокс согласия на ПДн */}
                                    <FormField
                                        control={form.control}
                                        name="acceptPrivacy"
                                        render={({ field }) => (
                                            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                                <FormControl>
                                                    <Checkbox
                                                        checked={field.value}
                                                        onCheckedChange={field.onChange}
                                                        className="border-[hsl(var(--portal-border-hover))] data-[state=checked]:bg-[hsl(var(--portal-accent))] data-[state=checked]:text-[hsl(var(--portal-accent-on))] data-[state=checked]:border-[hsl(var(--portal-accent))] h-5 w-5 mt-0.5 rounded-none"
                                                    />
                                                </FormControl>
                                                <div className="space-y-1 leading-none pt-0.5">
                                                    <FormLabel className="text-xs uppercase tracking-wide text-[hsl(var(--portal-text-secondary))] cursor-pointer">
                                                        Я согласен на{" "}
                                                        <Link
                                                            href="/consent"
                                                            target="_blank"
                                                            className="text-[hsl(var(--portal-accent))] underline underline-offset-2 hover:text-[hsl(var(--portal-accent-hover))]"
                                                        >
                                                            обработку персональных данных
                                                        </Link>
                                                        {" "}в соответствии с{" "}
                                                        <Link
                                                            href="/privacy"
                                                            target="_blank"
                                                            className="text-[hsl(var(--portal-accent))] underline underline-offset-2 hover:text-[hsl(var(--portal-accent-hover))]"
                                                        >
                                                            Политикой конфиденциальности
                                                        </Link>
                                                    </FormLabel>
                                                </div>
                                            </FormItem>
                                        )}
                                    />

                                    <div className="pt-4 flex gap-4">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={prevStep}
                                            className="h-12 border-[hsl(var(--portal-border))] bg-transparent text-[hsl(var(--portal-text))] hover:bg-[hsl(var(--portal-surface-2))] rounded-none uppercase tracking-widest font-bold text-xs px-6"
                                        >
                                            НАЗАД
                                        </Button>
                                        <Button
                                            className="flex-1 h-12 bg-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent-on))] hover:bg-[hsl(var(--portal-accent-hover))] rounded-none uppercase tracking-widest font-bold text-xs transition-colors"
                                            type="submit"
                                            disabled={isLoading}
                                        >
                                            {isLoading && (
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            )}
                                            РАЗВЕРНУТЬ СИСТЕМУ
                                        </Button>
                                    </div>
                                </div>
                            </form>
                        </Form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

export default function RegisterPage() {
    return (
        <Suspense fallback={
            <div className="min-h-screen w-full bg-[hsl(var(--portal-bg))] flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-[hsl(var(--portal-accent))] animate-spin" />
            </div>
        }>
            <RegisterForm />
        </Suspense>
    );
}
